import Flatpickr from 'stimulus-flatpickr'
import { localTimeZone, formatDateTimeWithLocalTimeZone, convertDateTimeToTimeZone } from '@app/javascript/utils/timezone_utils'

/**
 * @namespace InputDatetime
 * {@link https://github.com/adrienpoly/stimulus-flatpickr}
 */
export default class extends Flatpickr {
  /**
     * Configure the datepicker
     * {@link: https://github.com/adrienpoly/stimulus-flatpickr#date-and-time-formats}
     * {@link: https://flatpickr.js.org/formatting/#date-formatting-tokens}
     * {@link: https://flatpickr.js.org/formatting/#time-formatting-tokens}
     */

  static values = {
    enableTime: Boolean,
    formatInputWithLocalTimezone: Boolean,
    defaultDatetime: String
  }

  initialize() {
    if (this.hasDefaultDatetimeValue && this.formatInputWithLocalTimezoneValue) {
      const defaultDatetimeValueTimezone = 'America/New_York' // defaultDatetimeValue time zone. EST due to Rails config.
      this.instanceTarget.value = convertDateTimeToTimeZone(this.defaultDatetimeValue, defaultDatetimeValueTimezone, localTimeZone(), 'Y-M-D HH:mm')
    }

    this.element[this.identifier] = this.interface

    const isTimeEnabled = this.hasEnableTimeValue ? this.enableTimeValue : true
    this.config = {
      altInput: true,
      altFormat: isTimeEnabled ? 'n/j/Y h:iK' : 'n/j/Y',
      dateFormat: isTimeEnabled ? 'Y-m-d H:i' : 'Y-m-d',
      enableTime: isTimeEnabled
    }
  }

  // Define here anything that should be available for external controllers
  interface = {
    setDate: (date) => {
      this.fp.setDate(date)
    },
    clearDate: () => {
      this.fp.clear()
    }
  }

  /**
     * Because we're extending the Flapickr controller, we have access to their events & hooks.
     * And because we're using an "alternate" input to format the datetime for users,
     * (vs. the format that gets submitted to the server)
     * we need to manually assign the Flatpickr-generated input an ID and label.
     * This is so we can meet accessibility requirements.
     * @param selectedDates
     * @param dateString
     * @param instance
     * {@link https://github.com/adrienpoly/stimulus-flatpickr#extends-the-controller}
     */
  ready(selectedDates, dateString, instance) {
    // Create an ID for the Flatpickr input
    const atomicInputId = $(instance.input).attr('id')
    const flatpickrInputId = `${atomicInputId}--interactive`

    // Create a label for the Flatpickr input
    const atomicInputLabel = $(`label[for="${atomicInputId}"]`).text().trim()
    const atomicInputLabelMinusAsterisk = atomicInputLabel.slice(0, atomicInputLabel.indexOf('*'))
    const flatpickrInputLabel = `${atomicInputLabelMinusAsterisk} Date & Time`

    // Assign ID & label to the Flatpickr input
    $(instance.altInput).attr({
      id: flatpickrInputId,
      'aria-label': flatpickrInputLabel
    })

    this.formatInstanceInputWithLocalTimezone(instance)
  }

  valueUpdate(selectedDates, dateStr, instance) {
    this.formatInstanceInputWithLocalTimezone(instance)
  }

  // PRIVATE
  formatInstanceInputWithLocalTimezone(instance) {
    if (this.formatInputWithLocalTimezoneValue) {
      instance.input.value = formatDateTimeWithLocalTimeZone(instance.input.value)
    }
  }
}
